export class DeviceFingerprint {
  private cachedFingerprint: string | null = null;

  /**
   * Generate device fingerprint for license security
   * Uses available browser and Office.js APIs to create a unique identifier
   */
  async generate(): Promise<string> {
    if (this.cachedFingerprint) {
      return this.cachedFingerprint;
    }

    try {
      const components = await this.collectComponents();
      const fingerprint = await this.hashComponents(components);
      
      this.cachedFingerprint = fingerprint;
      return fingerprint;
      
    } catch (error) {
      console.warn('Device fingerprinting failed, using fallback:', error);
      
      // Fallback to basic fingerprint
      const fallback = this.generateFallbackFingerprint();
      this.cachedFingerprint = fallback;
      return fallback;
    }
  }

  /**
   * Collect device and environment components for fingerprinting
   */
  private async collectComponents(): Promise<Record<string, unknown>> {
    const components: Record<string, unknown> = {};

    // Browser information
    components.userAgent = navigator.userAgent;
    components.language = navigator.language;
    components.platform = navigator.platform;
    components.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    components.timezoneOffset = new Date().getTimezoneOffset();

    // Screen information
    if (screen) {
      components.screenResolution = `${screen.width}x${screen.height}`;
      components.colorDepth = screen.colorDepth;
      components.pixelDepth = screen.pixelDepth;
    }

    // Office.js specific information
    try {
      if (Office && Office.context) {
        components.officeHost = Office.context.host;
        components.officePlatform = Office.context.platform;
        
        // Office version information
        if (Office.context.diagnostics) {
          components.officeVersion = Office.context.diagnostics.version;
          components.officeHost = Office.context.diagnostics.host;
        }
        
        // Document information (anonymized)
        if (Office.context.document) {
          components.documentMode = Office.context.document.mode;
        }
      }
    } catch (error) {
      console.warn('Failed to collect Office.js information:', error);
    }

    // Hardware concurrency
    if (navigator.hardwareConcurrency) {
      components.hardwareConcurrency = navigator.hardwareConcurrency;
    }

    // Memory information (if available)
    if ('memory' in performance) {
      const memory = (performance as Performance & { memory?: { jsHeapSizeLimit: number } }).memory;
      if (memory) {
        components.memoryLimit = Math.round(memory.jsHeapSizeLimit / 1024 / 1024); // MB
      }
    }

    // Canvas fingerprinting (basic)
    try {
      components.canvas = await this.getCanvasFingerprint();
    } catch (error) {
      console.warn('Canvas fingerprinting failed:', error);
    }

    // WebGL information
    try {
      components.webgl = this.getWebGLFingerprint();
    } catch (error) {
      console.warn('WebGL fingerprinting failed:', error);
    }

    return components;
  }

  /**
   * Generate canvas-based fingerprint component
   */
  private async getCanvasFingerprint(): Promise<string> {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      throw new Error('Canvas context not available');
    }

    canvas.width = 200;
    canvas.height = 50;

    // Draw some shapes and text
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('Bifrost 🔒', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('Device ID', 4, 45);

    return canvas.toDataURL().slice(-50); // Last 50 chars for efficiency
  }

  /**
   * Generate WebGL-based fingerprint component
   */
  private getWebGLFingerprint(): string {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    
    if (!gl) {
      throw new Error('WebGL context not available');
    }

    const glContext = gl as WebGLRenderingContext;
    const renderer = glContext.getParameter(glContext.RENDERER);
    const vendor = glContext.getParameter(glContext.VENDOR);
    
    return `${vendor}|${renderer}`.slice(0, 100); // Limit length
  }

  /**
   * Hash fingerprint components using Web Crypto API
   */
  private async hashComponents(components: Record<string, unknown>): Promise<string> {
    const data = JSON.stringify(components, Object.keys(components).sort());
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    
    // Use subtle crypto for hashing
    const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    
    return hashHex.slice(0, 32); // Use first 32 characters
  }

  /**
   * Generate fallback fingerprint when advanced methods fail
   */
  private generateFallbackFingerprint(): string {
    const components = [
      navigator.userAgent || 'unknown',
      navigator.language || 'unknown',
      screen?.width || 0,
      screen?.height || 0,
      new Date().getTimezoneOffset(),
      Math.random().toString(36).substr(2, 9) // Add some randomness
    ];

    const data = components.join('|');
    
    // Simple hash function for fallback
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    return Math.abs(hash).toString(36).padStart(8, '0');
  }

  /**
   * Clear cached fingerprint (force regeneration)
   */
  clearCache(): void {
    this.cachedFingerprint = null;
  }
}